import React, { useState, useEffect } from 'react';
import { FaExclamationTriangle, FaTimesCircle, FaClock } from 'react-icons/fa';
import axiosInstance from '../api/axiosInstance';

const WarningStatus = ({ userId = null }) => {
  const [warnings, setWarnings] = useState({
    activeWarnings: [],
    postStats: {},
    summary: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchWarnings();
  }, [userId]);

  const fetchWarnings = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/warnings/my');
      setWarnings(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Warnungen:', err);
      setError('Warnungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getWarningIcon = (type) => {
    return type === 'yellow' ? (
      <FaExclamationTriangle className="text-yellow-500" />
    ) : (
      <FaTimesCircle className="text-red-500" />
    );
  };

  const getWarningBadge = (type) => {
    return type === 'yellow' ? (
      <span className="badge badge-warning text-white">Gelbe Karte</span>
    ) : (
      <span className="badge badge-error text-white">Rote Karte</span>
    );
  };

  if (loading) {
    return (
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <div className="flex items-center">
            <span className="loading loading-spinner loading-sm mr-2"></span>
            Lade Warn-Status...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <FaTimesCircle />
        <span>{error}</span>
      </div>
    );
  }

  const { activeWarnings, postStats, summary } = warnings;

  // Keine aktiven Warnungen
  if (!summary.hasActiveWarnings) {
    return (
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h3 className="card-title text-green-600">
            <FaExclamationTriangle className="text-green-500" />
            Warn-Status
          </h3>
          <p className="text-green-600">✅ Keine aktiven Warnungen</p>
          {postStats.postsToday > 0 && (
            <p className="text-sm text-gray-600">
              Du hast heute {postStats.postsToday} Post{postStats.postsToday !== 1 ? 's' : ''} erstellt.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Aktive Warnungen */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h3 className="card-title text-red-600">
            <FaExclamationTriangle />
            Aktive Warnungen
          </h3>
          
          <div className="space-y-3">
            {activeWarnings.map((warning) => (
              <div 
                key={warning.id} 
                className={`alert ${warning.type === 'yellow' ? 'alert-warning' : 'alert-error'}`}
              >
                <div className="flex items-start gap-3">
                  {getWarningIcon(warning.type)}
                  <div className="flex-grow">
                    <div className="flex items-center gap-2 mb-1">
                      {getWarningBadge(warning.type)}
                      <span className="text-sm">
                        von {warning.issuedBy} am {formatDate(warning.issuedAt)}
                      </span>
                    </div>
                    <p className="font-medium">{warning.reason}</p>
                    {warning.expiresAt && (
                      <p className="text-sm opacity-75 flex items-center gap-1 mt-1">
                        <FaClock />
                        Läuft ab am {formatDate(warning.expiresAt)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Post-Limits bei gelber Karte */}
      {summary.hasYellowCard && (
        <div className="card bg-yellow-50 border border-yellow-200">
          <div className="card-body">
            <h4 className="card-title text-yellow-700">
              <FaExclamationTriangle className="text-yellow-600" />
              Posting-Beschränkung
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="stat">
                <div className="stat-title text-yellow-700">Posts heute</div>
                <div className="stat-value text-yellow-800">
                  {postStats.postsToday || 0}
                </div>
              </div>
              <div className="stat">
                <div className="stat-title text-yellow-700">Limit pro Tag</div>
                <div className="stat-value text-yellow-800">
                  {postStats.limit || 2}
                </div>
              </div>
            </div>
            
            {postStats.postsToday >= postStats.limit ? (
              <div className="alert alert-warning mt-4">
                <FaTimesCircle />
                <span>
                  Du hast dein Tageslimit erreicht. Du kannst in den nächsten 24 Stunden keine weiteren Posts erstellen.
                </span>
              </div>
            ) : (
              <div className="text-yellow-700 mt-2">
                Du kannst noch {postStats.limit - postStats.postsToday} Post{postStats.limit - postStats.postsToday !== 1 ? 's' : ''} heute erstellen.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Vollständige Sperre bei roter Karte */}
      {summary.hasRedCard && (
        <div className="alert alert-error">
          <FaTimesCircle />
          <div>
            <h4 className="font-bold">Account gesperrt</h4>
            <p>Du bist vollständig gesperrt und kannst keine Inhalte erstellen oder auf das Forum zugreifen.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarningStatus;
